// Beworbene Landingpages je Projekt festlegen — mit automatischer Angle-Analyse.
//
// Nicos Arbeitsweise: eine Seite, mehrere Angles, daraus eine Kampagne mit einer
// Anzeigengruppe je Angle. Hier legt er die Seite fest, das System findet die
// Angles: erst aus der Deklaration der Website (/nlt-ads.json bzw.
// nlt-angles-Meta), sonst per Analyse des Seiteninhalts.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Loader2, Megaphone, Plus, RefreshCw, Trash2, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addLandingpage,
  analyseLandingpage,
  deleteLandingpage,
  getLandingpages,
  setLandingpageAngles,
  type Landingpage,
} from '@/lib/analytics-api';

const QUELLE: Record<string, { label: string; cls: string }> = {
  manifest: { label: 'aus Ads-Manifest', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  meta: { label: 'aus Seiten-Meta', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  ki: { label: 'per Analyse erkannt', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  manuell: { label: 'von Hand', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

export default function LandingpagesPanel({
  projectId,
  onKampagne,
  seiteImFokus,
}: {
  projectId: string;
  /** Startet den Generator für genau diese Seite (Schritt 1 → Schritt 2). */
  onKampagne?: (url: string) => void;
  seiteImFokus?: string | null;
}) {
  const queryClient = useQueryClient();
  const [neu, setNeu] = useState('');
  const [bearbeite, setBearbeite] = useState<string | null>(null);
  const [angleText, setAngleText] = useState('');

  const key = ['landingpages', projectId];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => getLandingpages(projectId) });
  const setzen = (d: any) => queryClient.setQueryData(key, (alt: any) => ({ ...alt, ...d }));

  const addMut = useMutation({
    mutationFn: (url: string) => addLandingpage(projectId, url),
    onSuccess: (d) => {
      setzen(d);
      setNeu('');
      const q = QUELLE[d.quelle || '']?.label || d.quelle;
      toast.success(d.gefunden ? `${d.gefunden} Angle(s) gefunden — ${q}` : 'Angelegt, aber keine Angles erkannt');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const analyseMut = useMutation({
    mutationFn: (id: string) => analyseLandingpage(projectId, id),
    onSuccess: (d) => {
      setzen(d);
      toast.success(d.gefunden ? `${d.gefunden} Angle(s) — ${QUELLE[d.quelle || '']?.label || d.quelle}` : 'Keine Angles erkannt');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const anglesMut = useMutation({
    mutationFn: ({ id, angles }: { id: string; angles: string[] }) => setLandingpageAngles(projectId, id, angles),
    onSuccess: (d) => { setzen(d); setBearbeite(null); toast.success('Angles gespeichert'); },
    onError: (e: any) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => deleteLandingpage(projectId, id),
    onSuccess: (d) => { setzen(d); toast.success('Entfernt'); },
    onError: (e: any) => toast.error(e.message),
  });

  const laeuft = addMut.isPending || analyseMut.isPending || anglesMut.isPending || delMut.isPending;
  const seiten = data?.landingpages || [];
  const recruiting = data?.projekt_art === 'bewerbungen';

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Layers className="h-4 w-4 text-blue-600" /> Schritt 1 · Beworbene Landingpages
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Seite festlegen — die Angles findet das System selbst. Je Angle entsteht eine Anzeigengruppe.
          Danach je Seite die Kampagne bauen.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
      ) : (
        <div className="space-y-3 px-4 py-3">
          {data?.hinweis && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {data.hinweis}
            </p>
          )}

          {!recruiting && (
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={neu}
                onChange={(e) => setNeu(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && neu.trim()) addMut.mutate(neu.trim()); }}
                placeholder="https://kunde.de/fenster"
                disabled={laeuft}
                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
              <button
                onClick={() => neu.trim() && addMut.mutate(neu.trim())}
                disabled={laeuft || !neu.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {addMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Festlegen & analysieren
              </button>
              {data?.vorschlag && (
                <button
                  onClick={() => addMut.mutate(data.vorschlag!)}
                  disabled={laeuft}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                  title={data.vorschlag}
                >
                  <Wand2 className="h-3.5 w-3.5" /> Diese Landingpage übernehmen
                </button>
              )}
            </div>
          )}

          {seiten.length === 0 ? (
            <p className="py-4 text-center text-xs text-gray-400">
              {recruiting
                ? 'Für Recruiting-Projekte nicht vorgesehen.'
                : 'Noch keine Landingpage festgelegt. Der Kampagnen-Generator analysiert dann nur die Startseite.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {seiten.map((lp: Landingpage) => {
                const q = QUELLE[lp.angle_quelle || ''] || null;
                return (
                  <div key={lp.id} className="py-3">
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{lp.name || lp.url}</p>
                        <p className="truncate text-[11px] text-gray-400">{lp.url}</p>
                      </div>
                      {onKampagne && !recruiting && (
                        <button
                          onClick={() => onKampagne(lp.url)}
                          disabled={laeuft}
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium disabled:opacity-50 ${
                            seiteImFokus === lp.url
                              ? 'bg-rose-600 text-white hover:bg-rose-700'
                              : 'border border-rose-300 text-rose-700 hover:bg-rose-50'
                          }`}
                        >
                          <Megaphone className="h-3 w-3" />
                          {seiteImFokus === lp.url ? 'Im Generator gewählt' : 'Kampagne für diese Seite'}
                        </button>
                      )}
                      <button
                        onClick={() => analyseMut.mutate(lp.id)}
                        disabled={laeuft}
                        title="Angles neu bestimmen"
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {analyseMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Neu analysieren
                      </button>
                      <button
                        onClick={() => { setBearbeite(lp.id); setAngleText((lp.angles || []).join(', ')); }}
                        disabled={laeuft}
                        className="rounded-lg border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Angles ändern
                      </button>
                      <button
                        onClick={() => { if (window.confirm(`„${lp.name || lp.url}" aus den beworbenen Seiten entfernen?`)) delMut.mutate(lp.id); }}
                        disabled={laeuft}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>

                    {bearbeite === lp.id ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          value={angleText}
                          onChange={(e) => setAngleText(e.target.value)}
                          placeholder="Fenster Sanierung, Fenster Neubau"
                          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => anglesMut.mutate({ id: lp.id, angles: angleText.split(',').map((a) => a.trim()).filter(Boolean) })}
                          disabled={laeuft}
                          className="rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          Speichern
                        </button>
                        <button
                          onClick={() => setBearbeite(null)}
                          className="rounded-lg px-2 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50"
                        >
                          Abbrechen
                        </button>
                        <p className="w-full text-[11px] text-gray-400">Mit Komma trennen. Je Angle entsteht eine Anzeigengruppe.</p>
                      </div>
                    ) : (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {(lp.angles || []).length ? (
                          (lp.angles || []).map((a) => (
                            <span key={a} className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-700">
                              {a}
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-amber-700">Keine Angles erkannt — von Hand setzen</span>
                        )}
                        {q && (
                          <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${q.cls}`}>{q.label}</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
